import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { QueryObserver, type QueryKey } from '@tanstack/react-query';
import { queryClient } from './query-client';

/**
 * A read on the app's ONE `queryClient` singleton, without a `QueryClientProvider` in the tree — for hooks that run
 * inside chrome rendered on its own (the chat header renders in tests and pre-provider shells; `useQuery` there
 * throws "No QueryClient set"). Same cache, same keys, same dedup as `useQuery`; returns the data only.
 */
export function useObservedQuery<T>(options: {
  queryKey: QueryKey;
  queryFn: () => Promise<T>;
  enabled: boolean;
  staleTime?: number;
  retry?: boolean;
}): { data: T | undefined; isLoading: boolean; isError: boolean; refetch: () => Promise<unknown> } {
  const [observer] = useState(() => new QueryObserver<T, unknown, T, T, QueryKey>(queryClient, options));
  useEffect(() => {
    observer.setOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key and the gate are what change a read
  }, [observer, JSON.stringify(options.queryKey), options.enabled]);
  const subscribe = useCallback((onChange: () => void) => observer.subscribe(() => onChange()), [observer]);
  const read = useCallback(() => observer.getCurrentResult().data, [observer]);
  // The status as a string (a stable snapshot), so a caller can draw loading and error without the whole result.
  const readStatus = useCallback(() => {
    const r = observer.getCurrentResult();
    return r.isError ? 'error' : r.isLoading ? 'loading' : 'ready';
  }, [observer]);
  const status = useSyncExternalStore(subscribe, readStatus, readStatus);
  const refetch = useCallback(() => observer.refetch(), [observer]);
  return { data: useSyncExternalStore(subscribe, read, read), isLoading: status === 'loading', isError: status === 'error', refetch };
}
